import { useEffect, useRef } from "react";
import { useDebounceValue, useEventListener, useInterval } from "usehooks-ts";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "utilities/firebase";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { useAuthStore } from "../stores/authStore";
import { useIsLoggedIn } from "./useIsLoggedIn";
import * as Session from "../services/session";

const REFRESH_INTERVAL = 30 * 60 * 1000;

const AUTHED_STATUSES = ["authenticated", "impersonating"];

const parseFirebaseToken = (raw: string): string | null => {
  try {
    const parsed = JSON.parse(raw);
    const status = parsed?.state?.status;
    if (!AUTHED_STATUSES.includes(status)) return null;
    return parsed?.state?.session?.firebase?.token ?? null;
  } catch {
    return null;
  }
};

const stripVolatileFields = (raw: string | null) => {
  if (!raw) return null;
  try {
    const { state } = JSON.parse(raw);
    // these fields are volatile and can change at any time
    // we should not be detecting session changes based on them
    delete state?.session?.lastRefreshedAt;
    delete state?.session?.firebase?.token;
    delete state?.session?.firebase?.expires;
    delete state?.session?.attributes?.zendeskMessengerToken;
    return JSON.stringify(state);
  } catch {
    return raw;
  }
};

const hasMeaningfulChange = (
  oldValue: string | null,
  newValue: string | null,
): boolean => stripVolatileFields(oldValue) !== stripVolatileFields(newValue);

export const useSessionSync = () => {
  const isLoggedIn = useIsLoggedIn();
  const { profile, isLoading: isProfileLoading } = useProfile();
  const isRefreshing = useAuthStore((s) => s.isRefreshing);
  const [debouncedUpdatedAt] = useDebounceValue(profile?.updatedAt, 300);

  const hasInitialized = useRef(false);
  const lastProfileUpdate = useRef(profile?.updatedAt);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    Session.initialize();
  }, []);

  useInterval(
    () => {
      Session.refresh();
    },
    isLoggedIn ? REFRESH_INTERVAL : null,
  );

  useEventListener("storage", async (event) => {
    if (event.key !== "auth-storage") return;
    if (!hasMeaningfulChange(event.oldValue, event.newValue)) return;

    // If another tab signed in, set up Firebase Auth on this tab BEFORE
    // rehydrating Zustand — otherwise Firestore subscriptions fire before
    // Firebase Auth has propagated and the permission-denied error triggers signOut
    const prevStatus = useAuthStore.getState().status;
    const wasAuthed =
      prevStatus === "authenticated" || prevStatus === "impersonating";

    if (!wasAuthed && event.newValue) {
      const firebaseToken = parseFirebaseToken(event.newValue);
      if (firebaseToken) {
        await signInWithCustomToken(auth, firebaseToken).catch(() => {});
      }
    }

    useAuthStore.persist.rehydrate();
  });

  useEffect(() => {
    if (isProfileLoading || !profile || isRefreshing) return;

    if (lastProfileUpdate.current === debouncedUpdatedAt) return;

    lastProfileUpdate.current = debouncedUpdatedAt;
    Session.refresh();
  }, [debouncedUpdatedAt, isProfileLoading, isRefreshing, profile]);
};
